<template>
  <div class="rtc-container">
    <div class="control-row">
      <el-button
        size="small"
        type="primary"
        :loading="roomStatus === 'entering'"
        :disabled="roomStatus === 'entered'"
        @click="handleEnterRoom"
      >
        进入房间
      </el-button>
      <el-button size="small" :loading="roomStatus === 'exiting'" @click="handleExit">离开房间</el-button>
      <el-button size="small" :disabled="roomStatus !== 'entered' || micStatus === 'starting'" @click="toggleMic">
        {{ micStatus === "started" ? "关闭麦克风" : "开启麦克风" }}
      </el-button>
      <el-button size="small" :disabled="roomStatus !== 'entered' || camStatus === 'starting'" @click="toggleCam">
        {{ camStatus === "started" ? "关闭摄像头" : "开启摄像头" }}
      </el-button>
      <el-button
        size="small"
        :disabled="roomStatus !== 'entered' || camStatus !== 'started'"
        @click="handleCaptureSnapshot"
      >
        视频帧
      </el-button>
      <el-button
        size="small"
        type="primary"
        plain
        :disabled="roomStatus !== 'entered' || shareStatus === 'sharing' || shareStatus === 'shared'"
        :loading="shareStatus === 'sharing'"
        @click="handleStartScreenShare"
      >
        开始共享屏幕
      </el-button>
      <el-button
        size="small"
        type="primary"
        plain
        :disabled="roomStatus !== 'entered' || shareStatus !== 'shared'"
        :loading="shareStatus === 'stopping'"
        @click="handleStopScreenShare"
      >
        停止共享屏幕
      </el-button>
    </div>

    <div v-if="showInviteLink" class="invite-link-container">
      <div class="invite-tip">
        复制链接邀请好友加入视频通话，一条链接仅可邀请一人，复制后自动更新链接。
      </div>
      <el-input class="invite-input" v-model="inviteLink" readonly>
        <template slot="prepend">
          <el-tooltip :visible-arrow="false" effect="dark" content="已复制" placement="bottom" :manual="true" v-model="showCopiedTip">
            <span class="invite-btn" @click="handleCopyInviteLink">复制</span>
          </el-tooltip>
        </template>
      </el-input>
    </div>

    <div class="streams">
      <div class="local">
        <div class="stream-title">本地画面</div>
        <div id="local" class="stream-box"></div>
      </div>
      <div class="remote">
        <div class="stream-title">远端画面</div>
        <div class="remote-grid">
          <div v-for="id in remoteUsersViews" :key="id" :id="id" class="remote-box"></div>
          <el-empty v-if="!remoteUsersViews.length" description="暂无远端用户" />
        </div>
      </div>
    </div>

    <div v-if="snapshotUrl" class="snapshot-panel">
      <div class="snapshot-title">当前视频帧</div>
      <img class="snapshot-img" :src="snapshotUrl" alt="视频帧预览" />
    </div>

    <div class="log-panel" ref="logContainer">
      <div class="log-title">Log</div>
      <div v-for="(item, idx) in logList" :key="idx" class="log-item">
        <span class="log-state" v-if="item.type === 'success'">🟩</span>
        <span class="log-state" v-else>🟥</span>
        <span class="log-text">{{ item.log }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import rtc from "./mixins/rtc.js";
import LibGenerateTestUserSig from "@/utils/trtc/lib-generate-test-usersig.min.js";

export default {
  name: "TrtcRoom",
  mixins: [rtc],
  props: {
    type: {
      type: String, // host | invite
      default: "host",
    },
    sdkAppId: Number,
    userId: String,
    strRoomId: String,
    cameraId: String,
    microphoneId: String,
    userSig: String,
    sdkSecretKey: String, // prototype only
    inviteUserSig: String,
  },
  data() {
    return {
      userSigLocal: "",
      inviteLink: "",
      showCopiedTip: false,
      snapshotUrl: "",
    };
  },
  computed: {
    isHostMode() {
      return this.type !== "invite";
    },
    showInviteLink() {
      return this.isHostMode && this.roomStatus === "entered" && !!this.inviteLink;
    },
  },
  methods: {
    isDeviceNotFoundError(e) {
      const name = e?.name || "";
      const msg = String(e?.message || "");
      return name === "NotFoundError" || /no\s+camera\s+detected/i.test(msg) || /no\s+microphone\s+detected/i.test(msg);
    },
    getDeviceErrorText(e, type) {
      const name = e?.name || "";
      const msg = String(e?.message || "");
      if (name === "NotAllowedError") return `${type}权限被拒绝，请在浏览器地址栏权限设置中允许后重试`;
      if (name === "NotFoundError") return `未检测到${type}设备，将以无${type}模式进入`;
      if (/no\s+camera\s+detected/i.test(msg)) return "未检测到摄像头，将以无摄像头模式进入";
      if (/no\s+microphone\s+detected/i.test(msg)) return "未检测到麦克风，将以无麦克风模式进入";
      return `${type}启动失败：${msg || "unknown"}`;
    },
    generateInviteLink() {
      if (!this.isHostMode) return;
      if (!this.sdkAppId || !this.sdkSecretKey || !this.strRoomId) return;

      const inviteUserId = `user_${parseInt(Math.random() * 100000000, 10)}`;
      const gen = new LibGenerateTestUserSig(Number(this.sdkAppId), String(this.sdkSecretKey), 604800);
      const inviteUserSig = gen.genTestUserSig(inviteUserId);
      const base = `${location.origin}${location.pathname}#/invite`;
      this.inviteLink = encodeURI(
        `${base}?sdkAppId=${this.sdkAppId}&userSig=${inviteUserSig}&strRoomId=${this.strRoomId}&userId=${inviteUserId}`
      );
    },
    async handleCopyInviteLink() {
      try {
        await navigator.clipboard.writeText(this.inviteLink);
      } catch (_) {
        // fallback
        const ta = document.createElement("textarea");
        ta.value = this.inviteLink;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      this.showCopiedTip = true;
      setTimeout(() => (this.showCopiedTip = false), 1200);
      this.generateInviteLink();
    },
    async handleEnterRoom() {
      if (!this.sdkAppId) {
        this.$message.error("请输入 sdkAppId");
        return;
      }
      if (!this.userId || !this.strRoomId) {
        this.$message.error("请输入 userId 和 strRoomId");
        return;
      }
      let sig = this.userSig;
      if (!this.isHostMode) {
        sig = this.inviteUserSig || sig;
      } else if (!sig && this.sdkSecretKey) {
        try {
        const gen = new LibGenerateTestUserSig(Number(this.sdkAppId), String(this.sdkSecretKey), 604800);
        sig = gen.genTestUserSig(String(this.userId));
        } catch (e) {
          this.$message.error(`生成 userSig 失败：${e?.message || "unknown"}`);
          return;
        }
      }
      if (!sig) {
        this.$message.error("请输入 userSig（或填写 sdkSecretKey 自动生成，正式版应由后端下发）");
        return;
      }
      this.userSigLocal = sig;

      try {
        await this.enterRoom();
        // 原型演示：允许无摄像头/无麦克风进入，设备失败不阻断入房
        try {
          await this.startLocalAudio();
        } catch (e) {
          // 原型演示：放行，不提示
        }
        try {
          await this.startLocalVideo();
        } catch (e) {
          // 原型演示：放行，不提示
        }
        // 原型演示：放行，不提示
        this.generateInviteLink();
      } catch (e) {
        this.$message.error(`进入房间失败：${e?.message || "unknown"}`);
      }
    },
    async handleExit() {
      try {
        await this.exitRoom();
        this.$message.success("已离开房间");
      } catch (e) {
        this.$message.error(`离开房间失败：${e?.message || "unknown"}`);
      }
    },
    async toggleMic() {
      try {
        if (this.micStatus === "started") await this.stopLocalAudio();
        else await this.startLocalAudio();
      } catch (e) {
        // 原型演示：放行，不提示
      }
    },
    async toggleCam() {
      try {
        if (this.camStatus === "started") await this.stopLocalVideo();
        else await this.startLocalVideo();
      } catch (e) {
        // 原型演示：放行，不提示
      }
    },
    async handleStartScreenShare() {
      try {
        await this.startScreenShare();
      } catch (e) {
        this.$message.error(`共享屏幕失败：${e?.message || "unknown"}`);
      }
    },
    async handleStopScreenShare() {
      try {
        await this.stopScreenShare();
      } catch (e) {
        this.$message.error(`停止共享失败：${e?.message || "unknown"}`);
      }
    },
    handleCaptureSnapshot() {
      if (!this.trtc) {
        this.$message.error("请先进入房间并开启摄像头");
        return;
      }
      try {
        // 调试：打印当前房间及本地推流状态
        console.log("[Snapshot] roomStatus:", this.roomStatus);
        console.log("[Snapshot] camStatus:", this.camStatus);
        console.log("[Snapshot] micStatus:", this.micStatus);
        console.log("[Snapshot] sdkAppId:", this.sdkAppId);
        console.log("[Snapshot] userId:", this.userId);
        console.log("[Snapshot] strRoomId:", this.strRoomId);
        console.log("[Snapshot] cameraId:", this.cameraId);
        console.log("[Snapshot] microphoneId:", this.microphoneId);
        console.log("[Snapshot] trtc instance:", this.trtc);

        const frameData = this.trtc.getVideoSnapshot();
        console.log("[Snapshot] raw frameData:", frameData);

        if (!frameData) {
          this.$message.warning("暂时无法获取视频帧，请确认本地视频已开启并正在播放。");
          return;
        }
        this.snapshotUrl = frameData;
        this.addSuccessLog("Get local video snapshot success.");
      } catch (e) {
        this.$message.error(`获取视频帧失败：${e?.message || "unknown"}`);
      }
    },
  },
  beforeDestroy() {
    // Ensure exit on destroy to release devices.
    this.exitRoom();
  },
};
</script>

<style scoped>
.rtc-container {
  width: 100%;
}
.control-row {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}
.invite-link-container {
  margin-top: 12px;
  border: 1px solid #b6d4fe;
  background: #cfe2ff;
  border-radius: 12px;
  padding: 10px 12px;
  color: #084298;
}
.invite-tip {
  font-size: 12px;
  line-height: 1.5;
}
.invite-input {
  margin-top: 8px;
}
.invite-btn {
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  font-weight: 800;
  padding: 0 6px;
}
.streams {
  margin-top: 12px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.4fr);
  gap: 12px;
}
@media (max-width: 980px) {
  .streams {
    grid-template-columns: 1fr;
  }
}
.stream-title {
  font-size: 12px;
  font-weight: 900;
  color: #111827;
  margin-bottom: 8px;
}
.stream-box {
  width: 100%;
  aspect-ratio: 16/9;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  background: #0b1220;
}
.remote-grid {
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}
@media (max-width: 520px) {
  .remote-grid {
    grid-template-columns: 1fr;
  }
}
.remote-box {
  width: 100%;
  aspect-ratio: 16/9;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  background: #0b1220;
}
.snapshot-panel {
  margin-top: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.92);
  padding: 10px 12px;
}
.snapshot-title {
  font-size: 12px;
  font-weight: 900;
  color: #111827;
  margin-bottom: 8px;
}
.snapshot-img {
  width: 100%;
  max-width: 640px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  display: block;
}
.log-panel {
  margin-top: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.92);
  padding: 10px 12px;
  max-height: 220px;
  overflow: auto;
}
.log-title {
  font-size: 12px;
  font-weight: 900;
  color: #111827;
  margin-bottom: 8px;
}
.log-item {
  display: flex;
  gap: 6px;
  font-size: 12px;
  color: #374151;
  line-height: 1.5;
}
.log-state {
  width: 18px;
  flex: 0 0 auto;
}
.log-text {
  word-break: break-word;
}
</style>

